import { Box, Center, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { AppHead } from "@/components/layouts"
import { AdminOnly, AppLink, AppLoader, AppTitle, Content, HowItWorks } from "@/components/utils"
import { useAPIResource, useBreakpoints } from "@/hooks"
import { peopleURL, userAPI } from "@/utils"

import { ProfileAnniversary } from "./ProfileAnniversary"
import { ProfileBirthday } from "./ProfileBirthday"
import { ProfileDetails } from "./ProfileDetails"
import { ProfileFeedbacks } from "./ProfileFeedbacks"
import { ProfileInformation } from "./ProfileInformation"
import { ProfileNote } from "./ProfileNote"
import { ProfileSessions } from "./ProfileSessions"
import { ProfileStats } from "./ProfileStats"
import { ProfileTeam } from "./ProfileTeam"
import { ProfileVacation } from "./ProfileVacation"

interface IProps {
  userId: string
}

export function Profile({ userId }: IProps) {
  const { t } = useTranslation()
  const gridParams = useBreakpoints(layout)
  const { data: user, isPending } = useAPIResource(userAPI, { id: userId })

  if (isPending) return <AppLoader />
  if (!user)
    return (
      <Center h="50vh" p="xl">
        <Text c="dimmed" fz="xl" fw={500} ta="center">
          {t("Employee not found")}
        </Text>
      </Center>
    )

  return (
    <>
      <AppHead title={user.full_name || user.name} />
      <AppTitle fz={32} align="center" gap={6}>
        <AppLink href={peopleURL}>{t("People")}</AppLink>
        <Text c="dimmed" fz="xl">
          /
        </Text>
        {user?.full_name}
        <HowItWorks ml="auto">
          <Content type="profile" />
        </HowItWorks>
      </AppTitle>
      <Box
        style={{
          display: "grid",
          gridAutoRows: "min-content",
          ...gridParams,
        }}
      >
        <ProfileInformation user={user} />
        <ProfileDetails user={user} />
        {user.is_active && (
          <>
            <ProfileTeam user={user} />
            <ProfileBirthday user={user} />
            <ProfileAnniversary user={user} />
            <ProfileVacation user={user} />
          </>
        )}
        <AdminOnly>
          <ProfileStats user={user} />
        </AdminOnly>
        <ProfileSessions user={user} />
        <ProfileFeedbacks user={user} />
        {<ProfileNote user={user} />}
      </Box>
    </>
  )
}

const xs = { gridTemplateColumns: "1fr", gridGap: "1rem" }
const layout = {
  xs,
  sm: {
    gridTemplateColumns: "1fr 1fr",
  },
  lg: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  xl: {
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
  },
}
